import { Component, type ErrorInfo, type ReactNode } from 'react';
import { getLogger } from '../../../libs/logger';
import { StorageKeys } from '../../../shared/constants/storageKeys';

const logger = getLogger('ChunkLoadErrorBoundary');

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
};

type State = {
  error: Error | undefined;
};

const isChunkLoadError = (error: Error): boolean =>
  error.name === 'ChunkLoadError' ||
  /Loading chunk [^ ]+ failed/i.test(error.message);

export class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      logger.error('chunk load failed; attempting recovery', error, errorInfo);
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    if (!isChunkLoadError(error)) {
      throw error;
    }

    if (
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(StorageKeys.ChunkReloaded) === null
    ) {
      window.sessionStorage.setItem(StorageKeys.ChunkReloaded, '1');
      window.location.reload();
      return null;
    }

    return this.props.fallback ?? null;
  }
}
